import { defineComponent as _defineComponent } from 'vue'
import CcIcon from '@/views/Automation/CampaignCreatorV2/components/CcIcon/CcIcon.ts.vue';
import CcActionsPopover from '@/views/Automation/CampaignCreatorV2/components/CcPopover/CcActionsPopover.ts.vue';
import CcPopover from '@/views/Automation/CampaignCreatorV2/components/CcPopover/CcPopover.ts.vue';
import CcRenameModal from '@/views/Automation/CampaignCreatorV2/components/Modals/CcRenameModal.ts.vue';
import { getContainerInlineStyles } from '@/views/Automation/CampaignCreatorV2/helpers';
import useAbGroups from '@/views/Automation/CampaignCreatorV2/store/AbGroups';
import useABVariableStore from '@/views/Automation/CampaignCreatorV2/store/AbVariables';
import useAdStore from '@/views/Automation/CampaignCreatorV2/store/Ad';
import useAdSetStore from '@/views/Automation/CampaignCreatorV2/store/AdSet';
import useCampaignStore from '@/views/Automation/CampaignCreatorV2/store/Campaign';
import useCreativeStore from '@/views/Automation/CampaignCreatorV2/store/Creative';
import useNavigationStore from '@/views/Automation/CampaignCreatorV2/store/Navigation';
import useTargetingStore from '@/views/Automation/CampaignCreatorV2/store/Targeting';
import { ABGroupMetadata, SupportedEntities } from '@/views/Automation/CampaignCreatorV2/store/Types';
import { MessageIcon, ViewLevelAction } from '@/views/Automation/CampaignCreatorV2/types';
import { AdABGroups } from '@/views/Automation/CampaignCreatorV2/validation/Ad';
import { AdSetAbGroups } from '@/views/Automation/CampaignCreatorV2/validation/AdSet';
import { CampaignAbGroupTypes } from '@/views/Automation/CampaignCreatorV2/validation/Campaign';
import { FacebookAbGroups } from '@/views/Automation/CampaignCreatorV2/validation/FacebookAbGroups';
import { PopoverTriggers, Position } from '@sh/types';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';

interface Props {
  abGroupType: CampaignAbGroupTypes | AdSetAbGroups | AdABGroups;
  entity: SupportedEntities;
}

interface Emits {
  (e: 'onNewGroupAdd'): void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CcVariableTags.ts',
  props: {
    abGroupType: null,
    entity: null
  },
  emits: ["onNewGroupAdd"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'onNewGroupAdd'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  abGroupType: CampaignAbGroupTypes | AdSetAbGroups | AdABGroups;
  entity: SupportedEntities;
};





const isRenameModalOpen = ref(false);

const abGroupStore = useAbGroups();
const navigationStore = useNavigationStore();
const abVariablesStore = useABVariableStore();
const abGroupGetter = storeToRefs(abGroupStore);

const uniqueIdentifier = computed(() => `${props.abGroupType}-${props.entity}`);

function getMessage(ab: ABGroupMetadata<SupportedEntities>): NullableType<MessageIcon> {
  const errors = abVariablesStore.getAbGroupErrors(props.entity, ab.id);

  if (errors.MISSING?.length) {
    return {
      content: errors.MISSING,
      icon: {
        type: 'uncompletedStatus',
        styles: {
          color: 'var(--cc-blue-500)',
        },
      },
    };
  }

  if (errors.BLOCKING?.length) {
    return {
      content: errors.BLOCKING,
      icon: {
        type: 'errorCircle',
        styles: {
          color: 'var(--cc-error-color)',
        },
      },
      containerStyles: {
        active: {
          '--title-color': 'var(--cc-error-color)',
          '--focus-color': '#d33f3f25',
        },
      },
    };
  }
}

const tags = computed(() => {
  const abGroups = abGroupGetter.getAbGroupForEntity.value(props.entity);

  if (!abGroups) {
    return [];
  }

  const currentItem = navigationStore.getCurrentAbGroupFor(props.entity, props.abGroupType);

  const abForCurrentGroup = Object.values(abGroups) as unknown as ABGroupMetadata<SupportedEntities>[];
  // current item should be first
  const currentAbGroups = abForCurrentGroup.filter((ab) => ab.typeId === props.abGroupType);

  const currentItemIndex = currentAbGroups.findIndex((ab) => ab.id === currentItem);

  let orderItems = [...currentAbGroups];
  if (currentItemIndex > 2) {
    const selectedGroup = currentAbGroups[currentItemIndex];
    const otherGroups = currentAbGroups.filter((ab) => ab.id !== selectedGroup.id);
    const [first, second, ...rest] = otherGroups;
    orderItems = [first, second, selectedGroup, ...rest];
  }

  return orderItems.map((ab) => {
    const actions: ViewLevelAction[][] = [
      [
        {
          name: 'Rename',
          event: 'rename-abGroup',
          disabled: false,
        },
      ],
      [
        {
          name: 'Clone',
          event: 'clone-abGroup',
          disabled: false,
        },
        {
          name: 'Delete',
          event: 'delete-abGroup',
          disabled: currentAbGroups.length === 1,
        },
      ],
    ];

    return {
      title: ab.name,
      value: ab.id,
      id: ab.id,
      actions,
      message: getMessage(ab),
    };
  });
});

const moreTags = computed(() => tags.value.slice(3));

const isMoreTagsError = computed(() => moreTags.value.some((tag) => tag.message));

const activeTab = computed(() => {
  const { entity, abGroupType } = props;
  const value = navigationStore.getCurrentAbGroupFor(entity, abGroupType);
  return tags.value.find((tag) => tag.id === value);
});

const currentGroup = computed(() => {
  switch (props.entity) {
    case SupportedEntities.campaign:
      return useCampaignStore();
    case SupportedEntities.adset:
      return useAdSetStore();
    case SupportedEntities.ad:
      return useAdStore();
    case SupportedEntities.targeting:
      return useTargetingStore();
    case SupportedEntities.creative:
      return useCreativeStore();
    default:
      return null;
  }
});

function onTagClick(id: string) {
  const { entity, abGroupType } = props;
  navigationStore.setInternalNavigation(entity, abGroupType, id);
}

function onAddNewGroup() {
  const { entity, abGroupType } = props;
  const groupMetadata = FacebookAbGroups[abGroupType as keyof typeof FacebookAbGroups];

  abGroupStore.addAbGroup(entity, {
    description: '',
    name: `${groupMetadata.readableName} Group ${tags.value.length + 1}`,
    entity,
    typeId: abGroupType,
  });

  emits('onNewGroupAdd');
}

function dispatchEvent(action: ViewLevelAction<string, any>, groupId: string) {
  const nextItem = tags.value.find((tag) => tag.id !== groupId);

  if (!currentGroup.value) {
    console.error('No current group found');
    return;
  }

  switch (action.event) {
    case 'clone-abGroup':
      // Clone Variables
      currentGroup.value.cloneABGroup(groupId);
      // Set navigation to current group
      break;
    case 'rename-abGroup':
      isRenameModalOpen.value = true;
      break;
    case 'delete-abGroup':
      // Set navigation to next group
      if (nextItem) {
        navigationStore.setInternalNavigation(props.entity, props.abGroupType, nextItem.id);
      } else {
        console.log('Cannot remove last group');
        return;
      }
      useAbGroups().deleteGroup(props.entity, groupId);
      // Delete Variables
      currentGroup.value.deleteABGroup(groupId);
      break;
  }
}

function onTitleChange(value: string | KeyboardEvent) {
  if (!value) return;
  isRenameModalOpen.value = false;

  const newname = (value as Event).target ? ((value as KeyboardEvent).target as any)?.value : value;
  abGroupStore.renameGroup(props.entity, { groupId: activeTab.value?.id || '', name: newname });
}

function selectTagFromDropdown(id: string) {
  onTagClick(id);
}

onMounted(() => {
  // If no abGroup available, create one
  if (tags.value.length === 0) {
    onAddNewGroup();
  }
});

return { __sfc: true,props, emits, isRenameModalOpen, abGroupStore, navigationStore, abVariablesStore, abGroupGetter, uniqueIdentifier, getMessage, tags, moreTags, isMoreTagsError, activeTab, currentGroup, onTagClick, onAddNewGroup, dispatchEvent, onTitleChange, selectTagFromDropdown, CcIcon, CcActionsPopover, CcPopover, CcRenameModal, getContainerInlineStyles, PopoverTriggers, Position }
}

})